import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '../ui/use-toast';
import { CheckCircle } from 'lucide-react';
import { LEARN_STRIPE_CONFIG, calculateTotalWithGST } from '@/config/learnStripeConfig';

interface LearnEnrollmentFormProps {
  isOpen: boolean;
  onClose: () => void;
  productType: 'online' | 'faceToFace';
  couponCode?: string;
  onRedirecting?: () => void;
}

const LearnEnrollmentForm: React.FC<LearnEnrollmentFormProps> = ({
  isOpen,
  onClose,
  productType,
  couponCode: initialCouponCode = '',
  onRedirecting,
}) => {
  const [fullName, setFullName] = useState('');
  const [email, setEmail] = useState('');
  const [couponCode, setCouponCode] = useState(initialCouponCode);
  const [organizationId, setOrganizationId] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [paymentStatus, setPaymentStatus] = useState<'idle' | 'success'>('idle');
  const { toast } = useToast();

  const product = LEARN_STRIPE_CONFIG.products[productType];
  const totalPrice = calculateTotalWithGST(product.price);

  useEffect(() => {
    const fetchUserEmail = async () => {
      const { data: { session } } = await supabase.auth.getSession();
      if (session?.user?.email) {
        setEmail(session.user.email);
      }
    };
    fetchUserEmail();
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!fullName.trim() || !email.trim()) {
      toast({
        title: 'Missing Information',
        description: 'Please fill in all required fields',
        variant: 'destructive',
      });
      return;
    }

    setIsSubmitting(true);
    let isRedirecting = false;

    try {
      const { data: { session } } = await supabase.auth.getSession();
      const userId = session?.user?.id || null;

      // Create enrollment record
      const { data: enrollment, error: enrollError } = await supabase
        .from('course_enrollments')
        .insert({
          course_id: 'ai-for-smes-chamber',
          email: email.trim(),
          amount: product.price,
          payment_status: 'pending',
          user_id: userId,
          is_subscription: false,
          discount_code: couponCode.trim() || null,
          enrollment_token: crypto.randomUUID(),
          expires_at: new Date(Date.now() + 365 * 24 * 60 * 60 * 1000).toISOString(),
        })
        .select('id, enrollment_token')
        .maybeSingle();

      if (enrollError) {
        console.error('Enrollment insert error:', enrollError);
        throw new Error(`Failed to create enrollment: ${enrollError.message}`);
      }
      
      if (!enrollment) {
        throw new Error('Failed to create enrollment record');
      }

      // Fetch payment link from database
      const { data: paymentLinkData, error: paymentLinkError } = await supabase
        .from('payment_links')
        .select('payment_link_url')
        .eq('product_id', product.productId)
        .eq('active', true)
        .maybeSingle();

      if (paymentLinkError || !paymentLinkData?.payment_link_url) {
        console.error('Payment link error:', paymentLinkError);
        throw new Error('Payment link not configured. Please contact support.');
      }

      // Set flag to prevent further state updates
      isRedirecting = true;

      // Open payment link in new tab
      window.open(paymentLinkData.payment_link_url, '_blank');
      
      // Close modal
      onClose();
      setIsSubmitting(false);
      
    } catch (error: any) {
      // Only update state if we're not redirecting
      if (!isRedirecting) {
        console.error('Enrollment error:', error);
        toast({
          title: 'Enrollment Failed',
          description: error.message || 'Please try again or contact support',
          variant: 'destructive',
        });
        setIsSubmitting(false);
      }
    }
  };

  if (paymentStatus === 'success') {
    return (
      <Dialog open={isOpen} onOpenChange={onClose}>
        <DialogContent>
          <div className="text-center py-6">
            <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
            <h3 className="text-xl font-semibold mb-2">Payment Successful!</h3>
            <p className="text-gray-600">Check your email for access instructions.</p>
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Enroll in {product.name}</DialogTitle>
        </DialogHeader>

        <div className="mb-4 p-4 bg-gray-50 rounded-lg">
          <div className="flex justify-between items-center mb-2">
            <span className="text-gray-600">Base Price:</span>
            <span className="font-semibold">${product.price} NZD</span>
          </div>
          <div className="flex justify-between items-center mb-2">
            <span className="text-gray-600">GST (15%):</span>
            <span className="font-semibold">${(product.price * 0.15).toFixed(2)}</span>
          </div>
          <div className="flex justify-between items-center pt-2 border-t border-gray-300">
            <span className="font-bold">Total:</span>
            <span className="font-bold text-brand-teal">${totalPrice.toFixed(2)} NZD</span>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <Label htmlFor="fullName">Full Name *</Label>
            <Input
              id="fullName"
              value={fullName}
              onChange={(e) => setFullName(e.target.value)}
              placeholder="John Smith"
              required
            />
          </div>

          <div>
            <Label htmlFor="email">Email *</Label>
            <Input
              id="email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="john@example.com"
              required
            />
          </div>

          <div>
            <Label htmlFor="organizationId">Organization ID (Optional)</Label>
            <Input
              id="organizationId"
              value={organizationId}
              onChange={(e) => setOrganizationId(e.target.value)}
              placeholder="e.g., marlborough-chamber-2024"
            />
            <p className="text-xs text-gray-500 mt-1">For group enrollments or Chamber members</p>
          </div>

          <div>
            <Label htmlFor="couponCode">Coupon Code (Optional)</Label>
            <Input
              id="couponCode"
              value={couponCode}
              onChange={(e) => setCouponCode(e.target.value)}
              placeholder="EARLYBIRD2024"
            />
          </div>

          <Button
            type="submit"
            className="w-full"
            disabled={isSubmitting}
          >
            {isSubmitting ? 'Processing...' : `Proceed to Checkout - $${totalPrice.toFixed(2)}`}
          </Button>

          <p className="text-xs text-center text-gray-500">
            Secure payment via Stripe. You'll be redirected to complete your purchase.
          </p>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default LearnEnrollmentForm;
